/**
 * Shared pricing source of truth.
 *
 * Amounts are stored in major units here (₹249, $3, S$4) because the Pricing
 * page renders them directly and humans want to read them. The Stripe intent
 * call multiplies by 100 to get the minor-unit amount the backend expects.
 *
 * Keep this table in sync with backend/src/routes/payments.js PRICES — the
 * backend is the authority on what actually gets charged, but any drift here
 * means the Pricing page will display a different number than Checkout.
 */

export type Currency = 'usd' | 'inr' | 'sgd';
export type PlanId = 'try' | 'starter' | 'pro' | 'team';

export interface PlanPricing {
  id: PlanId;
  name: string;
  tagline: string;
  cadence: 'one-time' | 'month';
  credits?: number;
  monthlyCap?: number;
  features: string[];
  highlighted?: boolean;
  /** Major-unit price per currency (₹, $, S$). Keep integer-friendly. */
  amounts: Record<Currency, number>;
}

export const PLANS: PlanPricing[] = [
  {
    id: 'try',
    name: 'Try',
    tagline: 'Perfect for first-timers',
    cadence: 'one-time',
    credits: 5,
    amounts: { usd: 3, inr: 249, sgd: 4 },
    features: [
      '5 credits to evaluate or generate',
      'Section-by-section feedback',
      'Rubric grading included',
      'No subscription — try before you commit',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Best value per credit',
    cadence: 'one-time',
    credits: 20,
    amounts: { usd: 9, inr: 749, sgd: 12 },
    features: [
      '20 credits (evaluations, generations, rubrics)',
      'Section-by-section feedback',
      'Download results as Word / Markdown',
      'Email support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For frequent users',
    cadence: 'month',
    monthlyCap: 200,
    amounts: { usd: 19, inr: 1599, sgd: 25 },
    features: [
      '200 operations / month',
      '"Improve to A-grade" rewriter included',
      'Custom rubric library',
      'Priority processing (faster queue)',
      'Monthly progress reports',
    ],
    highlighted: true,
  },
  {
    id: 'team',
    name: 'Team',
    tagline: 'For classes and departments',
    cadence: 'month',
    monthlyCap: 1000,
    amounts: { usd: 49, inr: 3999, sgd: 65 },
    features: [
      '1,000 operations / month',
      '5 seats with shared credits',
      'Shared rubric library',
      'Team analytics dashboard',
      'SSO (Google Workspace)',
    ],
  },
];

export const CURRENCY_META: Record<Currency, {
  code: Currency;
  symbol: string;
  label: string;
  /** Timezones that default to this currency. */
  timezones: string[];
}> = {
  inr: { code: 'inr', symbol: '₹',  label: 'Indian Rupee',    timezones: ['Asia/Kolkata', 'Asia/Calcutta'] },
  sgd: { code: 'sgd', symbol: 'S$', label: 'Singapore Dollar', timezones: ['Asia/Singapore'] },
  usd: { code: 'usd', symbol: '$',  label: 'US Dollar',        timezones: [] }, // fallback
};

/**
 * Pick a default currency from the browser's resolved timezone.
 *
 * Timezone is more reliable than `navigator.language` for region detection
 * because users everywhere often keep `en-US` as their language but their
 * timezone reflects their actual location. Falls back to USD for anything
 * we don't explicitly recognise — safer than guessing and charging in a
 * currency the user can't hold.
 */
export function detectCurrencyFromTimezone(): Currency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (CURRENCY_META.inr.timezones.includes(tz)) return 'inr';
    if (CURRENCY_META.sgd.timezones.includes(tz)) return 'sgd';
  } catch {
    // Intl.DateTimeFormat should never throw in modern browsers, but guard
    // anyway — ancient WebViews have surfaced bugs here in the past.
  }
  return 'usd';
}

/** Lookup a plan by id — convenience so callers don't re-array-find. */
export function getPlan(id: PlanId): PlanPricing {
  const p = PLANS.find((x) => x.id === id);
  if (!p) throw new Error(`Unknown plan id: ${id}`);
  return p;
}

/** Major-unit amount for (plan, currency). */
export function priceOf(id: PlanId, currency: Currency): number {
  return getPlan(id).amounts[currency];
}

/**
 * Format a major-unit amount for display with the right symbol and locale.
 * Uses Intl so ₹ 1,599 vs. $19.00 vs. S$25 all come out properly grouped.
 */
export function formatPrice(amount: number, currency: Currency): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${CURRENCY_META[currency].symbol}${amount}`;
  }
}
